import { useState, useEffect, useCallback } from 'react';
import {
  generatePath,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  ajvResolver,
  SidePanelLoader,
  ValidatorsProps,
  responseErrorsResolver,
  ResponseError,
  Popup,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import isEqual from 'lodash.isequal';
import { clearBankAndPspInfoAccountFormState } from './store/slices/info-account-form';
import {
  useCreatePspAccountMutation,
  useLazyGetPspAccountQuery,
  useUpdatePspAccountMutation,
} from './store/services';
import AccountForm from '../../components/bank-and-psp/info-account-form';
import { PspAccountValues, PspAccountItem } from './types';
import { setNotificationVisible } from '../../../../../store/slice';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

let timeout: NodeJS.Timeout;

const BankAndPspInfoAccountForm = () => {
  const { bankId, accountId } = useParams();
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [requestIsAvailable, setRequestIsAvailable] = useState(true);
  const [defaultBankDetails, setDefaultBankDetails] = useState<{
    bankName?: string | null;
    bankLegalName?: string | null;
    bankCountryId?: string | null;
    bankAddress?: string | null;
    bankSortCode?: string | null;
    bankSwift?: string | null;
    bankAba?: string | null;
  }>();
  const [sortCode, setSortCode] = useState('');

  const {
    bankAndPsp: {
      info: { validators, generalInfo, casp, pspAccountsCategories },
      infoAccountForm: { formValues },
      accountPage: { generalInfo: accountGeneralInfo },
    },
  } = useAppSelector((state) => state);
  const {
    currenciesList: currencies,
    countriesList: countries,
    activePermissions,
  } = useAppSelector((state) => state.root);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    reset,
    setValue,
    watch,
    clearErrors,
  } = useForm<PspAccountValues>({
    resolver: ajvResolver(
      accountId
        ? ({} as ValidatorsProps)
        : (validators?.createPspAccountSchema as ValidatorsProps),
    ),
  });

  const [create, { isLoading: requestInProgress, isSuccess: createIsSuccess }] =
    useCreatePspAccountMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateIsSuccess }] =
    useUpdatePspAccountMutation();
  const [getAccount, { isLoading, isFetching }] = useLazyGetPspAccountQuery();

  const getAccountAction = useCallback(() => {
    getAccount(accountId as string);
  }, [accountId]);

  const fields = watch();

  const formIsDirty =
    isDirty || fields.bankSortCode !== formValues?.bankSortCode;

  const bankDetailsIsDirty = !isEqual(defaultBankDetails, {
    bankName: fields?.bankName,
    bankLegalName: fields?.bankLegalName,
    bankCountryId: fields?.bankCountryId,
    bankAddress: fields?.bankAddress,
    bankSortCode: fields?.bankSortCode,
    bankSwift: fields?.bankSwift,
    bankAba: fields?.bankAba,
  });

  const currencyId = watch('currencyId');

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onRetryAccount = useCallback(async (id: string) => {
    clearTimeout(timeout);

    const response = await getAccount(id);

    if (
      response?.data?.data &&
      response?.data?.data?.pspIntegrationAccountStatus === 'IN_PROGRESS'
    ) {
      timeout = setTimeout(() => {
        onRetryAccount(id);
      }, 15000);
    }
  }, []);

  const onFormSubmit = handleSubmit(async (values) => {
    let response;

    setGlobalError('');

    if (requestIsAvailable) {
      const body: PspAccountValues = {
        categoryId: values.categoryId,
        currencyType: values.currencyType,
        currencyId: values.currencyId || [],
        pspPartnerId: accountId ? values.pspPartnerId : (bankId as string),
        description: values.description,
        visibleToClient: accountId ? values.visibleToClient : true,
        excludeFromTopUpIfExternalAccountAvailable:
          values.excludeFromTopUpIfExternalAccountAvailable,
        useOwnBankDetails: values.useOwnBankDetails,
        bankAba: values?.bankAba,
      };

      if (values.useOwnBankDetails) {
        if (values?.bankName) {
          body.bankName = values?.bankName;
        }

        if (values?.bankLegalName) {
          body.bankLegalName = values?.bankLegalName;
        }

        if (values?.bankAddress) {
          body.bankAddress = values?.bankAddress;
        }
        if (values?.bankCountryId) {
          body.bankCountryId = values?.bankCountryId;
        }
        if (values?.bankSortCode) {
          body.bankSortCode = values?.bankSortCode;
        }
        if (values?.bankSwift) {
          body.bankSwift = values?.bankSwift;
        }
        if (values?.bankAba) {
          body.bankAba = values?.bankAba;
        }
      } else if (!values.useOwnBankDetails) {
        if (!values?.bankName && formValues?.bankName) {
          body.bankName = null;
        }
        if (!values?.bankLegalName && formValues?.bankLegalName) {
          body.bankLegalName = null;
        }
        if (!values?.bankCountryId && formValues?.bankCountryId) {
          body.bankCountryId = null;
        }
        if (!values?.bankAddress && formValues?.bankAddress) {
          body.bankAddress = null;
        }
        if (!values?.bankSortCode && formValues?.bankSortCode) {
          body.bankSortCode = null;
        }
        if (!values?.bankSwift && formValues?.bankSwift) {
          body.bankSwift = null;
        }
        if (!values?.bankAba && formValues?.bankAba) {
          body.bankAba = null;
        }
      }

      if (!accountId || (accountId && !accountGeneralInfo?.externalAccountId)) {
        body.realNumberOrIban = values.realNumberOrIban;
      }

      if (values.isDefaultForExternalOperations) {
        body.isDefaultForExternalOperations =
          values.isDefaultForExternalOperations;
      }

      if (values.pspPrimaryAccountId) {
        body.pspPrimaryAccountId = values.pspPrimaryAccountId;
      }

      if (values.accountOfCorrespondentWithIntermediaryBank) {
        body.accountOfCorrespondentWithIntermediaryBank =
          values.accountOfCorrespondentWithIntermediaryBank;
      } else if (
        !values.accountOfCorrespondentWithIntermediaryBank &&
        formValues?.accountOfCorrespondentWithIntermediaryBank
      ) {
        body.accountOfCorrespondentWithIntermediaryBank = '';
      }

      if (values.intermediaryBankName) {
        body.intermediaryBankName = values.intermediaryBankName;
      } else if (
        !values.intermediaryBankName &&
        formValues?.intermediaryBankName
      ) {
        body.intermediaryBankName = '';
      }

      if (values.intermediaryBankSwift) {
        body.intermediaryBankSwift = values.intermediaryBankSwift;
      } else if (
        !values.intermediaryBankSwift &&
        formValues?.intermediaryBankSwift
      ) {
        body.intermediaryBankSwift = '';
      }

      if (values?.caspId) {
        body.caspId = values.caspId;
      } else if (!values.caspId && formValues?.caspId) {
        body.caspId = null;
      }

      if (accountId) {
        response = (await update({
          body,
          id: accountId,
        })) as {
          data?: PspAccountItem;
          error: ResponseError;
        };
      } else {
        if (
          generalInfo?.pspIntegration &&
          (generalInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET' ||
            generalInfo?.pspIntegration?.name === 'OPEN_PAYD' ||
            generalInfo?.pspIntegration?.name === 'Keel') &&
          values.externalAccountBindType
        ) {
          body.externalAccountBindType = values.externalAccountBindType;
        }

        response = (await create(body)) as {
          data?: PspAccountItem;
          error: ResponseError;
        };
      }

      if (response && response?.error) {
        responseErrorsResolver({
          errors: response?.error,
          setError,
          setGlobalError,
        });
      }
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    if (accountId) {
      setTimeout(() => {
        navigate(
          `${generatePath(BankRoutes.BANK_AND_PSP_ACCOUNT_PAGE, {
            accountId,
          })}${search || ''}`,
        );
      }, 100);
    } else {
      setTimeout(() => {
        navigate(
          `${generatePath(BankRoutes.BANK_AND_PSP_ACCOUNTS, {
            bankId: bankId as string,
          })}${search || ''}`,
        );
      }, 100);
    }
  }, [accountId]);

  useEffect(() => {
    if (bankId && !accountId) {
      reset({ pspPartnerId: bankId, currencyType: 'ALL' });
    }
  }, [bankId, accountId]);

  useEffect(() => {
    if (updateIsSuccess && !globalError) {
      handleClose();
    }
  }, [updateIsSuccess, globalError]);

  useEffect(() => {
    if (createIsSuccess) {
      handleClose();
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Account creation in progress. You'll be notified.",
          type: 'psp-accounts-create',
        }),
      );
    }
  }, [createIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (accountId) {
      getAccountAction();
    }

    return () => {
      dispatch(clearBankAndPspInfoAccountFormState());
    };
  }, [dispatch, accountId, getAccountAction]);

  useEffect(() => {
    if (formValues && accountId && generalInfo) {
      reset({
        ...formValues,
        bankName: formValues?.bankName || generalInfo?.companyName,
        bankLegalName: formValues?.bankLegalName || generalInfo?.legalName,
        bankCountryId: formValues?.bankCountryId || generalInfo?.countryId,
        bankAddress: formValues?.bankAddress || generalInfo?.address,
        bankSortCode: formValues?.bankSortCode || generalInfo?.sortCode,
        bankSwift: formValues?.bankSwift || generalInfo?.swift,
        bankAba: formValues?.bankAba || generalInfo?.aba,
      });

      setDefaultBankDetails({
        bankName: formValues?.bankName || generalInfo?.companyName,
        bankLegalName: formValues?.bankLegalName || generalInfo?.legalName,
        bankCountryId: formValues?.bankCountryId || generalInfo?.countryId,
        bankAddress: formValues?.bankAddress || generalInfo?.address,
        bankSortCode: formValues?.bankSortCode || generalInfo?.sortCode,
        bankSwift: formValues?.bankSwift || generalInfo?.swift,
        bankAba: formValues?.bankAba || generalInfo?.aba,
      });
    }
  }, [reset, formValues, accountId, bankId, generalInfo]);

  useEffect(() => {
    if (!accountId && generalInfo) {
      reset({
        bankName: formValues?.bankName || generalInfo?.companyName,
        bankLegalName: formValues?.bankLegalName || generalInfo?.legalName,
        bankCountryId: formValues?.bankCountryId || generalInfo?.countryId,
        bankAddress: formValues?.bankAddress || generalInfo?.address,
        bankSortCode: formValues?.bankSortCode || generalInfo?.sortCode,
        bankSwift: formValues?.bankSwift || generalInfo?.swift,
        bankAba: formValues?.bankAba || generalInfo?.aba,
      });
    }
  }, [reset, accountId, generalInfo]);

  let buttonText = 'Add account';

  if (accountId) {
    buttonText = 'Save changes';
  }

  useEffect(() => {
    if (formValues?.bankSortCode || generalInfo?.sortCode) {
      setSortCode(
        (formValues?.bankSortCode as string) ||
          (generalInfo?.sortCode as string),
      );
    }
  }, [formValues?.bankSortCode, generalInfo?.sortCode]);

  let content = (
    <AccountForm
      control={control}
      errors={errors}
      itemId={accountId}
      isEditable={
        accountId
          ? activePermissions?.includes(PermissionEnum.PSP_ACCOUNT_EDIT_ACTION)
          : activePermissions?.includes(PermissionEnum.PSP_ACCOUNT_ADD_ACTION)
      }
      pspIntegrations={generalInfo?.pspIntegration}
      setValue={setValue}
      currenciesList={currencies}
      partnerType={generalInfo?.partnerType}
      partnerLocaction={generalInfo?.location}
      currencyId={currencyId}
      casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
      bankCaspId={generalInfo?.caspId}
      accountCaspId={formValues?.caspId}
      pspAccountsCategories={pspAccountsCategories}
      externalAccountId={accountGeneralInfo?.externalAccountId}
      fields={fields}
      countries={countries}
      setError={setError}
      clearErrors={clearErrors}
      setRequestIsAvailable={setRequestIsAvailable}
      generalInfo={generalInfo}
      accountId={accountId}
      bankDetailsIsDirty={bankDetailsIsDirty}
      formValues={formValues}
      sortCode={sortCode}
      setSortCode={setSortCode}
    />
  );

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !isFetching &&
    ((!accountId &&
      !activePermissions?.includes(PermissionEnum.PSP_ACCOUNT_ADD_ACTION)) ||
      (accountId &&
        !activePermissions?.includes(PermissionEnum.PSP_ACCOUNT_EDIT_ACTION)))
  ) {
    content = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={accountId ? 'Account details' : 'Add account'}
        errorSidepanel={globalError}
        sidepanelExpandedKey="bank-account"
        footer={
          <>
            {((activePermissions?.includes(
              PermissionEnum.PSP_ACCOUNT_EDIT_ACTION,
            ) &&
              accountId) ||
              (activePermissions?.includes(
                PermissionEnum.PSP_ACCOUNT_ADD_ACTION,
              ) &&
                !accountId)) && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                bold
                disabled={
                  requestInProgress ||
                  updateInProgress ||
                  !formIsDirty ||
                  !requestIsAvailable
                }
                className={cn.accountFormSubmitButton}
              />
            )}
            <ButtonSecondary
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default BankAndPspInfoAccountForm;
